/**
 * gemini.js — Offset resolution for Gemini style-guide analysis
 *
 *
 * Gemini returns style-guide issues with:
 *   - paragraphId      (which paragraph)
 *   - original text    ("the university")
 *   - suggested text   ("The University")
 *   - contextBefore    ("...students at ")
 *   - contextAfter     (" will benefit...")
 *   - start/end = -1   (unresolved — Gemini doesn't compute offsets)
 *
 * We need to resolve EXACT character offsets (start/end) so we can:
 *   1. Highlight the correct text in the preview
 *   2. Apply replacements at the right position in the XML
 *
 * The challenge: the same phrase ("the university") might appear
 * multiple times in a paragraph. contextBefore/contextAfter is how
 * we disambiguate which occurrence Gemini meant.
 *
 * ═══════════════════════════════════════════════════════════
 *  RESOLUTION STRATEGIES (tried in priority order)
 * ═══════════════════════════════════════════════════════════
 *
 *  1. Full context   — concatenate before + original + after, find exact match
 *  2. Before context — find contextBefore, search for original right after it
 *  3. After context  — find contextAfter, search backwards for original before it
 *  4. Cursor search  — indexOf from cursor position (Nth occurrence tracking)
 *  5. Simple search  — indexOf from beginning (last resort)
 *
 * ═══════════════════════════════════════════════════════════
 *  OUTPUT FORMAT
 * ═══════════════════════════════════════════════════════════
 *
 * Each resolved issue has:
 *   {
 *     id:          string    — unique UUID (stable within a session)
 *     rule:        string    — style rule identifier
 *     message:     string    — human-readable explanation
 *     status:      'open'    — always 'open' initially
 *     paragraphId: number    — paragraph index in the document
 *     original:    string    — exact verbatim substring in the document
 *     suggested:   string    — recommended replacement
 *     start:       number    — resolved character offset (inclusive)
 *     end:         number    — resolved character offset (exclusive)
 *   }
 */

/* ── ID generator ── */
function generateUniqueId() {
  // Prefer crypto UUID when available (all modern browsers).
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Fallback pseudo-unique id for environments without crypto.randomUUID.
  return `id-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/* ══════════════════════════════════════════════════════════════
 *  Core offset resolution — tries 5 strategies in priority order
 * ══════════════════════════════════════════════════════════════ */

/**
 * Try to find `originalText` within `paragraphText` using context clues.
 *
 * @param {string} paragraphText  - Full text of the paragraph
 * @param {string} originalText   - The text to locate
 * @param {string} contextBefore  - Text that appears before original in Gemini's response
 * @param {string} contextAfter   - Text that appears after original in Gemini's response
 * @param {number} cursorPosition - Start searching from this position (for Nth occurrence)
 * @returns {{ start: number, end: number } | null}
 */
function findTextWithContext(
  paragraphText,
  originalText,
  contextBefore,
  contextAfter,
  cursorPosition = 0,
) {
  const originalLength = originalText.length;

  /*
   * Strategy 1: Full context match
   * Build the full signature "contextBefore + original + contextAfter"
   * and search for this exact combined string. Most precise — if the
   * full context exists in the paragraph, we know exactly where original is.
   */
  if (contextBefore || contextAfter) {
    const fullSignature = (contextBefore || '') + originalText + (contextAfter || '');
    const signatureIndex = paragraphText.indexOf(fullSignature);
    if (signatureIndex !== -1) {
      const startOffset = signatureIndex + (contextBefore || '').length;
      return { start: startOffset, end: startOffset + originalLength };
    }
  }

  /*
   * Strategy 2: Before context
   * Find where contextBefore appears, then look for original text
   * in a small window immediately after it.
   */
  if (contextBefore) {
    const beforeContextIndex = paragraphText.indexOf(contextBefore);
    if (beforeContextIndex !== -1) {
      const searchStartPosition = beforeContextIndex + contextBefore.length;
      // Limit window to originalLength + 20 chars to reduce false positives.
      const windowEndPosition = Math.min(
        paragraphText.length,
        searchStartPosition + originalLength + 20,
      );
      const searchWindow = paragraphText.slice(searchStartPosition, windowEndPosition);
      const matchIndex = searchWindow.indexOf(originalText);
      if (matchIndex !== -1) {
        const absoluteStart = searchStartPosition + matchIndex;
        return { start: absoluteStart, end: absoluteStart + originalLength };
      }
    }
  }

  /*
   * Strategy 3: After context (reverse search)
   * Find where contextAfter appears, then look backwards for original
   * text in the space just before it.
   */
  if (contextAfter) {
    const afterContextIndex = paragraphText.indexOf(contextAfter);
    if (afterContextIndex !== -1) {
      // First try: original is immediately adjacent to contextAfter.
      const expectedStart = afterContextIndex - originalLength;
      if (
        expectedStart >= 0 &&
        paragraphText.slice(expectedStart, afterContextIndex) === originalText
      ) {
        return { start: expectedStart, end: afterContextIndex };
      }
      // Second try: broader backward window (±20 chars).
      const windowStartPosition = Math.max(0, afterContextIndex - originalLength - 20);
      const searchWindow = paragraphText.slice(windowStartPosition, afterContextIndex);
      const matchIndex = searchWindow.lastIndexOf(originalText);
      if (matchIndex !== -1) {
        const absoluteStart = windowStartPosition + matchIndex;
        return { start: absoluteStart, end: absoluteStart + originalLength };
      }
    }
  }

  /*
   * Strategy 4: Cursor-based indexOf
   * Use the cursor position to skip past earlier occurrences that
   * were already claimed by previous issues with the same text.
   */
  const cursorMatchIndex = paragraphText.indexOf(originalText, cursorPosition);
  if (cursorMatchIndex !== -1) {
    return { start: cursorMatchIndex, end: cursorMatchIndex + originalLength };
  }

  /*
   * Strategy 5: Simple indexOf from beginning
   * Last resort — just find the first occurrence anywhere.
   */
  const simpleMatchIndex = paragraphText.indexOf(originalText);
  if (simpleMatchIndex !== -1) {
    return { start: simpleMatchIndex, end: simpleMatchIndex + originalLength };
  }

  // All strategies failed — text not found in this paragraph.
  return null;
}

/**
 * Check whether a resolved [start, end) character range overlaps any
 * hyperlink or field <w:t> nodes in the paragraph.
 *
 * We skip issues that target protected text because editing hyperlinks
 * or fields would corrupt the document structure.
 *
 * @param {number} paragraphId           - Which paragraph to check
 * @param {number} startOffset           - Start of the candidate range
 * @param {number} endOffset             - End of the candidate range
 * @param {Map}    textNodesByParagraph   - paragraphId → node metadata
 * @param {Map}    offsetMap              - paragraphId → offset ranges
 * @returns {boolean} true if the range touches protected text
 */
function touchesProtectedText(
  paragraphId,
  startOffset,
  endOffset,
  textNodesByParagraph,
  offsetMap,
) {
  const textNodesMetadata = textNodesByParagraph.get(paragraphId);
  const offsetRanges = offsetMap.get(paragraphId);
  if (!textNodesMetadata || !offsetRanges) return false;

  return offsetRanges.some((range) => {
    // Skip ranges that don't overlap the candidate span.
    if (range.end <= startOffset || range.start >= endOffset) return false;
    const textNode = textNodesMetadata[range.nodeIndex];
    return textNode?.isHyperlink || textNode?.isField;
  });
}

/* ══════════════════════════════════════════════════════════════
 *  Main entry point — resolve all Gemini issues to concrete offsets
 * ══════════════════════════════════════════════════════════════ */

/**
 * Resolve character offsets for all issues returned by Gemini.
 *
 * Filters out:
 *   - Issues with missing paragraphs or empty original text
 *   - No-op issues where original === suggested
 *   - Overlapping issues (keeps the first one found)
 *   - Issues targeting hyperlink or field text
 *
 * @param {Array} rawIssues              - Issues from Gemini (with context fields)
 * @param {Array} paragraphs             - Parsed paragraphs [{ paragraphId, text }]
 * @param {Map}   [textNodesByParagraph]  - Optional: for hyperlink filtering
 * @param {Map}   [offsetMap]             - Optional: for hyperlink filtering
 * @returns {Array} Resolved issues with start/end offsets, sorted by document position
 */
export function computeOffsets(rawIssues, paragraphs, textNodesByParagraph, offsetMap) {
  if (!rawIssues?.length || !paragraphs?.length) return [];

  // Build a quick lookup: paragraphId → paragraph text.
  const paragraphTextLookup = new Map();
  for (const paragraph of paragraphs) {
    paragraphTextLookup.set(paragraph.paragraphId, paragraph.text || '');
  }

  /*
   * Cursor tracking: when the same phrase appears multiple times in a
   * paragraph, we need to resolve each Gemini issue to a DIFFERENT occurrence.
   * The cursor advances past each claimed match so the next issue with the
   * same text finds the next occurrence.
   *
   * Key format: "paragraphId::originalText" → next search position
   */
  const searchCursors = new Map();

  /*
   * Claimed ranges: tracks which character spans have already been assigned
   * to resolved issues. New issues that overlap an existing claim are skipped
   * to prevent two issues from editing the same text.
   *
   * Key: paragraphId → [{ start, end }]
   */
  const claimedRanges = new Map();

  const resolvedIssues = [];

  for (const issue of rawIssues) {
    const paragraphText = paragraphTextLookup.get(issue.paragraphId);

    // ── Validation guards ──
    if (paragraphText === undefined) {
      console.warn(`[computeOffsets] Skipping: paragraphId ${issue.paragraphId} not found`);
      continue;
    }
    if (!issue.original) {
      console.warn('[computeOffsets] Skipping: empty original text');
      continue;
    }
    if (issue.original === issue.suggested) {
      console.warn(`[computeOffsets] Skipping no-op: "${issue.original}" (original === suggested)`);
      continue;
    }

    // ── Resolve character offsets using context-driven strategies ──
    const cursorKey = `${issue.paragraphId}::${issue.original}`;
    const currentCursor = searchCursors.get(cursorKey) || 0;

    const matchResult = findTextWithContext(
      paragraphText,
      issue.original,
      issue.contextBefore || '',
      issue.contextAfter || '',
      currentCursor,
    );

    if (!matchResult) {
      console.warn(
        `[computeOffsets] Skipping: "${issue.original}" not found in paragraph ${issue.paragraphId}`,
      );
      continue;
    }

    // ── Overlap check: reject if this range is already claimed ──
    const paragraphId = issue.paragraphId;
    if (!claimedRanges.has(paragraphId)) claimedRanges.set(paragraphId, []);
    const existingRanges = claimedRanges.get(paragraphId);
    const hasOverlap = existingRanges.some(
      (existingRange) =>
        !(matchResult.end <= existingRange.start || matchResult.start >= existingRange.end),
    );

    if (hasOverlap) {
      console.warn(
        `[computeOffsets] Skipping overlapping: "${issue.original}" at [${matchResult.start}:${matchResult.end}]`,
      );
      continue;
    }

    // ── Protected text check: reject if range touches hyperlinks/fields ──
    if (textNodesByParagraph && offsetMap) {
      if (
        touchesProtectedText(
          paragraphId,
          matchResult.start,
          matchResult.end,
          textNodesByParagraph,
          offsetMap,
        )
      ) {
        console.warn(
          `[computeOffsets] Skipping hyperlink/field text: "${issue.original}" at [${matchResult.start}:${matchResult.end}]`,
        );
        continue;
      }
    }

    // ── Claim this range and advance the cursor ──
    existingRanges.push({ start: matchResult.start, end: matchResult.end });
    searchCursors.set(cursorKey, matchResult.end);

    // ── Emit the fully-resolved issue object ──
    resolvedIssues.push({
      id: generateUniqueId(),
      rule: issue.rule,
      message: issue.message,
      status: 'open',
      paragraphId: issue.paragraphId,
      original: issue.original,
      suggested: issue.suggested,
      start: matchResult.start,
      end: matchResult.end,
    });
  }

  // Sort by document position (paragraph first, then character offset).
  resolvedIssues.sort((a, b) => a.paragraphId - b.paragraphId || a.start - b.start);
  return resolvedIssues;
}
